import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ClipboardCheck, Download, Loader2 } from "lucide-react";
import { AnalysisJobState } from "@/components/NextFitAnalysisReport";

const SEV = { alta: "text-rose-700 bg-rose-50 border-rose-200", media: "text-amber-800 bg-amber-50 border-amber-200", baixa: "text-slate-700 bg-slate-50 border-slate-200" };

const KV = ({ title, data, testid }) => (
  <div className="rounded border p-2 text-[11px]" data-testid={testid}>
    <div className="font-bold mb-1">{title}</div>
    {Object.entries(data || {}).map(([k, v]) => (
      <div key={k} className="break-all">{k}: <b>{typeof v === "object" ? JSON.stringify(v) : String(v)}</b></div>
    ))}
  </div>
);

const Grid = ({ data }) => {
  const keys = ["ativo", "suspenso", "encerrado", "inadimplente", "cadastro_inativo", "catraca_liberada"];
  return (
    <table className="w-full text-[11px] border" data-testid="audit-status-grid">
      <thead className="bg-slate-50"><tr><th className="px-2 py-1 text-left">Situação</th><th className="px-2 text-left">Next Fit</th><th className="px-2 text-left">American Fit</th></tr></thead>
      <tbody>{keys.map((k) => (
        <tr key={k} className="border-t"><td className="px-2 py-0.5 font-semibold">{k}</td><td className="px-2">{data?.next_fit?.[k] ?? "—"}</td><td className="px-2">{data?.american_fit?.[k] ?? "—"}</td></tr>
      ))}</tbody>
    </table>
  );
};

const Divergence = ({ d }) => (
  <details className={`rounded border p-2 text-[11px] ${SEV[d.severidade] || ""}`} data-testid={`audit-div-${d.tipo}`}>
    <summary className="cursor-pointer font-bold">{d.tipo} · {d.severidade} · {d.quantidade}</summary>
    <div className="mt-1 space-y-1 max-h-72 overflow-auto">
      {d.exemplos.map((e, i) => <div key={i} className="border-t pt-1">{Object.entries(e).filter(([k]) => !["tipo", "severidade"].includes(k)).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>)}
    </div>
  </details>
);

const downloadCsv = async (name) => {
  try {
    const { data } = await api.get(`/integrations/nextfit/finance-audit/csv/${name}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data); a.download = name; a.click();
  } catch (e) { toast.error("CSV indisponível — rode a auditoria novamente."); }
};

const AccessReview = ({ data }) => {
  if (!data) return null;
  return (
    <div className="rounded border border-rose-200 bg-rose-50/40 p-3 text-[11px]" data-testid="audit-access-review">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-bold text-sm">Catraca liberada sem contrato válido: {data.alunos} aluno(s)</div>
        <Button size="sm" variant="outline" onClick={() => downloadCsv(data.csv)} data-testid="audit-access-csv-btn"><Download className="w-4 h-4 mr-1"/>CSV da catraca</Button>
      </div>
      <div className="text-slate-600 mb-1">{data.observacao}</div>
      {Object.entries(data.por_classificacao || {}).map(([k, v]) => <div key={k} data-testid={`audit-access-cls-${k}`}>{k}: <b>{v}</b></div>)}
      {Object.entries(data.por_motivo || {}).map(([k, v]) => <div key={k}>{k}: <b>{v}</b></div>)}
      <div className="mt-1 space-y-1 max-h-60 overflow-auto">{(data.exemplos || []).map((e, i) => <div key={i} className="border-t pt-1"><b>{e.classificacao}</b> · {e.aluno} · CPF {e.cpf} · {e.motivo} · {e.causa}</div>)}</div>
    </div>
  );
};

export const NextFitFinanceAudit = ({ unitId }) => {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const poll = async (id) => {
    setBusy(true);
    for (let i = 0; i < 1500; i++) {
      try {
        const { data } = await api.get(`/integrations/nextfit/jobs/${id}`);
        setJob(data);
        if (["done", "failed", "interrupted"].includes(data.status)) break;
      } catch { break; }
      await new Promise((r) => setTimeout(r, 3000));
    }
    setBusy(false);
  };
  useEffect(() => {
    api.get(`/integrations/nextfit/units/${unitId}/finance-audit`).then(({ data }) => {
      setJob(data.job);
      if (data.job && ["pending", "running"].includes(data.job.status)) poll(data.job.job_id);
    }).catch(() => setJob(null));
    /* eslint-disable-next-line */
  }, [unitId]);

  const start = async () => {
    try { const { data } = await api.post(`/integrations/nextfit/units/${unitId}/finance-audit`); setJob(data); poll(data.job_id); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };
  const r = job?.report;
  const download = () => downloadCsv(r.csv);

  return (
    <Card className="p-5" data-testid="finance-audit-card">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-slate-700"/><h3 className="font-heading text-lg font-black text-slate-900">Auditoria final (somente leitura)</h3></div>
        <div className="flex gap-2">
          <Button onClick={start} disabled={busy} variant="outline" data-testid="finance-audit-btn">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <ClipboardCheck className="w-4 h-4 mr-1"/>}Rodar auditoria</Button>
          {r?.csv && <Button onClick={download} variant="outline" data-testid="finance-audit-csv-btn"><Download className="w-4 h-4 mr-1"/>CSV completo ({r.linhas_csv})</Button>}
        </div>
      </div>
      <p className="text-xs text-slate-500">Compara Next Fit × American Fit sem gravar nada em alunos ou pagamentos.</p>
      <AnalysisJobState job={job}/>
      {r && (
        <div className="mt-3 space-y-3" data-testid="finance-audit-report">
          <div className="text-xs font-bold">AUDITORIA v{r.versao || 1} — nada foi gravado · {r.pagamentos_a_gravar} pagamentos previstos · {r.generated_at?.slice(0, 16)}</div>
          {r.correcoes_necessarias && (
            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-[11px]" data-testid="audit-fixes">
              <div className="font-bold text-sm mb-1">Correções necessárias</div>
              <ul className="list-disc pl-4 space-y-0.5">{r.correcoes_necessarias.map((f, i) => <li key={i}>{f}</li>)}</ul>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            <KV title="1. Vínculo dos pagamentos (aluno, plano, data, valor, status)" data={r.vinculo_pagamentos} testid="audit-link"/>
            <div><div className="text-[11px] font-bold mb-1">7. Situação dos alunos</div><Grid data={r.comparacao_situacao}/><KV title="Acesso à catraca" data={r.acesso_catraca} testid="audit-access"/></div>
            <KV title="3. Cadastro dos alunos (complemento DESATIVADO — nada será alterado)" data={r.complemento_cadastro} testid="audit-patches"/>
            <KV title="4. Receita, caixa e cobrança" data={r.receita_caixa} testid="audit-revenue"/>
            <KV title="5. Títulos fora da gravação" data={r.titulos_fora_da_gravacao} testid="audit-excluded"/>
            <KV title="6. Pagamentos nativos do American Fit" data={r.pagamentos_nativos} testid="audit-natives"/>
            {r.analise_valores && <KV title="Diferenças de valor por causa" data={{ total: r.analise_valores.titulos_com_diferenca, ...r.analise_valores.por_causa }} testid="audit-values"/>}
            {r.integridade_pagamentos && <KV title="Integridade dos pagamentos previstos" data={r.integridade_pagamentos} testid="audit-integrity"/>}
            {r.descontos_simulacao && <KV title="Descontos integrais / cortesias" data={r.descontos_simulacao} testid="audit-discounts"/>}
            {r.agente_local_catraca && <KV title="Regra de acesso e agente local" data={r.agente_local_catraca} testid="audit-device"/>}
          </div>
          <AccessReview data={r.catraca_revisao}/>
          <div className="space-y-1" data-testid="audit-divergences">
            <div className="text-xs font-bold">Divergências (até 20 exemplos por tipo; lista completa no CSV)</div>
            {(r.divergencias || []).map((d) => <Divergence key={d.tipo} d={d}/>)}
          </div>
        </div>
      )}
    </Card>
  );
};
